import React, { useCallback } from 'react';
import { Check, AlertCircle, AlertTriangle, FileText, Trash2, Code, Maximize2 } from 'lucide-react';

interface JsonEditorProps {
  value: string;
  onChange: (value: string) => void;
  onExample: () => void;
  onClear: () => void;
  onOpenNormalize?: () => void;
  isLoading: boolean;
  validationResult: { valid: boolean; error?: string } | null;
  warnings: string[];
  error: string | null;
}

export function JsonEditor({ value, onChange, onExample, onClear, onOpenNormalize, isLoading, validationResult, warnings, error }: JsonEditorProps) {
  const formatJson = useCallback(() => {
    try {
      const parsed = JSON.parse(value);
      const formatted = JSON.stringify(parsed, null, 2);
      onChange(formatted);
    } catch (e) {
      // Se não for JSON válido, não faz nada
    }
  }, [value, onChange]);

  const lineCount = value.split('\n').length;
  const charCount = value.length;

  return (
    <div className="flex flex-col h-full border-b border-navy-700/30">
      {/* Header com ações */}
      <div className="flex items-center justify-between px-3 py-2.5 bg-navy-800/50 border-b border-navy-700/30">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-gray-200">
            JSON da Ação
          </label>
          {(lineCount > 0 || charCount > 0) && (
            <span className="text-[10px] text-gray-500 font-mono">
              {lineCount} linhas • {charCount} chars
            </span>
          )}
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <button
            onClick={formatJson}
            disabled={!value.trim() || isLoading}
            title="Formatar JSON (Ctrl+Shift+F)"
            className="px-2 py-1 text-[10px] bg-purple-600/20 text-purple-300 rounded hover:bg-purple-600/30 transition-all duration-200 border border-purple-600/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <Maximize2 className="w-3 h-3" />
            <span className="hidden sm:inline">Formatar</span>
          </button>
          <button
            onClick={onExample}
            disabled={isLoading}
            title="Carregar exemplo (Ctrl+E)"
            className="px-2 py-1 text-[10px] bg-blue-600/20 text-blue-300 rounded hover:bg-blue-600/30 transition-all duration-200 border border-blue-600/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <FileText className="w-3 h-3" />
            <span className="hidden sm:inline">Exemplo</span>
          </button>
          <button
            onClick={onClear}
            disabled={isLoading || !value.trim()}
            title="Limpar (Ctrl+K)"
            className="px-2 py-1 text-[10px] bg-gray-600/20 text-gray-300 rounded hover:bg-gray-600/30 transition-all duration-200 border border-gray-600/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <Trash2 className="w-3 h-3" />
            <span className="hidden sm:inline">Limpar</span>
          </button>
          {onOpenNormalize && (
            <button
              onClick={onOpenNormalize}
              disabled={isLoading}
              title="Normalizar código VB.NET"
              className="px-2 py-1 text-[10px] bg-green-600/20 text-green-300 rounded hover:bg-green-600/30 transition-all duration-200 border border-green-600/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <Code className="w-3 h-3" />
              <span className="hidden sm:inline">VB</span>
            </button>
          )}
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 relative min-h-[200px] sm:min-h-[256px]">
        {isLoading && !value.trim() ? (
          <div className="absolute inset-0 flex items-center justify-center bg-navy-900/30 z-10">
            <div className="flex flex-col items-center gap-2">
              <div className="w-5 h-5 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
              <span className="text-[10px] sm:text-xs text-gray-400">Validando...</span>
            </div>
          </div>
        ) : null}
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Cole ou digite o JSON da ação aqui..."
          className={`w-full h-full p-3 bg-navy-900/50 border-0 rounded-none text-xs sm:text-sm text-white placeholder-gray-500 font-mono resize-none focus:outline-none focus:ring-2 transition-all duration-200 ${
            isLoading ? 'opacity-70' : ''
          } ${
            error 
              ? 'focus:ring-red-500/50' 
              : validationResult?.valid 
                ? 'focus:ring-green-500/50' 
                : 'focus:ring-purple-500/50'
          }`}
          disabled={isLoading}
          style={{ 
            tabSize: 2,
            lineHeight: '1.5'
          }}
        />
      </div>

      {/* Status e validação */}
      <div className="px-3 py-2 bg-navy-800/30 space-y-2">
        {validationResult && (
          <div className="flex items-center gap-1.5 text-xs">
            {validationResult.valid ? (
              <div className="flex items-center gap-1.5 text-green-300 bg-green-500/10 px-2 py-1 rounded border border-green-500/20">
                <Check className="w-3.5 h-3.5" />
                <span className="font-medium">JSON Válido</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 text-red-300 bg-red-500/10 px-2 py-1 rounded border border-red-500/20 flex-1">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="font-medium truncate">{validationResult.error}</span>
              </div>
            )}
          </div>
        )}

        {warnings.length > 0 && (
          <details className="group">
            <summary className="flex items-center gap-1.5 text-yellow-300 text-xs bg-yellow-500/10 px-2 py-1 rounded border border-yellow-500/20 cursor-pointer hover:bg-yellow-500/15 transition-colors">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span className="font-medium">{warnings.length} aviso{warnings.length > 1 ? 's' : ''}</span>
              <span className="ml-auto text-[10px] opacity-70 group-open:hidden">(clique)</span>
            </summary>
            <div className="mt-2 space-y-1 pl-5">
              {warnings.map((warning, index) => (
                <div key={index} className="flex items-start gap-1.5 text-yellow-200 text-[11px]">
                  <span className="text-yellow-400 mt-0.5">•</span>
                  <span>{warning}</span>
                </div>
              ))}
            </div>
          </details>
        )}

        {error && !validationResult && (
          <div className="flex items-center gap-1.5 text-red-300 text-xs bg-red-500/10 px-2 py-1 rounded border border-red-500/20">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="font-medium">{error}</span>
          </div>
        )}
      </div>
    </div>
  );
}


